import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Eyebrow,
  Field,
  Input,
  Select,
  formatDate,
  formatTime,
  todayIn,
  useConfirm,
  type ConfirmRequest,
} from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { Column, DataTable, Drawer, StatusBadge } from '../components/DataTable.js';
import { errorMessage, useResource } from '../lib/useResource.js';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useT } from '../i18n/index.js';

interface Trip {
  id: string;
  departureAt: string;
  status: string;
  delayMinutes: number;
  note: string | null;
  capacity: number;
  seatsBooked: number;
  route: { id: string; name: string };
  bus: { id: string; name: string; plateNumber: string };
}

interface ManifestRow {
  seatCode: string;
  passengerName: string;
  reference: string;
  contactEmail: string;
  checkedInAt: string | null;
}

interface TripDetail extends Trip {
  manifest: ManifestRow[];
  route: Trip['route'] & {
    routeStops: Array<{ id: string; arrivalOffsetMin: number; stop: { name: string; city: string } }>;
  };
}

export function Trips() {
  const t = useT();
  const { tenant } = useSession();
  const timezone = tenant?.timezone ?? 'UTC';
  const [date, setDate] = useState(() => todayIn(timezone));
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [adHoc, setAdHoc] = useState(false);

  const query = new URLSearchParams({ date, pageSize: '100' });
  if (status) query.set('status', status);
  const trips = useResource<Trip[]>(`/admin/trips?${query.toString()}`);

  const columns: Column<Trip>[] = [
    {
      key: 'time',
      header: t('f.departs'),
      width: '7rem',
      render: (trip) => (
        <span className="board-time text-base">{formatTime(trip.departureAt, timezone)}</span>
      ),
    },
    { key: 'route', header: t('f.route'), render: (trip) => <span className="font-medium">{trip.route.name}</span> },
    { key: 'bus', header: t('f.bus'), render: (trip) => `${trip.bus.name} · ${trip.bus.plateNumber}` },
    {
      key: 'load',
      header: t('f.booked'),
      numeric: true,
      render: (trip) => (
        <span>
          {trip.seatsBooked}
          <span style={{ color: 'var(--color-faint)' }}> / {trip.capacity}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('f.status'),
      render: (trip) => (
        <span className="flex items-center gap-2">
          <StatusBadge status={trip.status} />
          {trip.delayMinutes > 0 && (
            <span className="text-xs tabular" style={{ color: 'var(--signal-delayed)' }}>
              +{trip.delayMinutes}m
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'note',
      header: t('f.note'),
      render: (trip) => <span style={{ color: 'var(--color-muted)' }}>{trip.note || '—'}</span>,
    },
  ];

  return (
    <Page
      title={t('nav.trips')}
      description={t('trips.subtitle', { date: formatDate(`${date}T12:00:00Z`, timezone), tz: timezone })}
      actions={<Button onClick={() => setAdHoc(true)}>{t('trips.addOneOff')}</Button>}
    >
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('f.date')} htmlFor="trip-date">
          <Input id="trip-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label={t('f.status')} htmlFor="trip-status">
          <Select id="trip-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{t('f.all')}</option>
            <option value="SCHEDULED">{t('st.scheduled')}</option>
            <option value="DELAYED">{t('st.delayed')}</option>
            <option value="DEPARTED">{t('st.departed')}</option>
            <option value="CANCELLED">{t('st.cancelled')}</option>
          </Select>
        </Field>
      </div>

      <DataTable
        columns={columns}
        rows={trips.data}
        rowKey={(trip) => trip.id}
        loading={trips.loading}
        error={trips.error}
        onRowClick={(trip) => setSelected(trip.id)}
        empty={{
          title: t('trips.emptyTitle'),
          description: t('trips.emptyBody'),
        }}
      />

      {selected && (
        <TripDetailDrawer
          tripId={selected}
          timezone={timezone}
          onClose={() => setSelected(null)}
          onChanged={async () => {
            await trips.reload();
          }}
        />
      )}
      {adHoc && (
        <AdHocDrawer
          timezone={timezone}
          onClose={() => setAdHoc(false)}
          onCreated={async () => {
            setAdHoc(false);
            await trips.reload();
          }}
        />
      )}
    </Page>
  );
}

function TripDetailDrawer({
  tripId,
  timezone,
  onClose,
  onChanged,
}: {
  tripId: string;
  timezone: string;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const t = useT();
  const trip = useResource<TripDetail>(`/admin/trips/${tripId}`);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [delayMinutes, setDelayMinutes] = useState(15);
  const [note, setNote] = useState('');
  const { ask, dialog } = useConfirm();

  /** Every action states its passenger impact before it happens (docs/06). */
  async function act(
    path: string,
    payload: Record<string, unknown>,
    confirm?: ConfirmRequest,
  ): Promise<void> {
    if (confirm && !(await ask(confirm))) return;
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      const result = await api.post<{ passengersNotified: number; bookingsAffected?: number }>(
        `/admin/trips/${tripId}/${path}`,
        payload,
      );
      setNotice(
        `Done. ${result.passengersNotified} passenger${result.passengersNotified === 1 ? '' : 's'} notified.`,
      );
      await trip.reload();
      await onChanged();
    } catch (err) {
      setActionError(errorMessage(err, 'Could not apply that change.'));
    } finally {
      setBusy(false);
    }
  }

  const booked = trip.data?.seatsBooked ?? 0;
  const departsWithinTwoHours = trip.data
    ? new Date(trip.data.departureAt).getTime() - Date.now() < 2 * 3600 * 1000
    : false;

  return (
    <Drawer open title={t('f.departure')} onClose={onClose}>
      {trip.loading && <p style={{ color: 'var(--color-muted)' }}>Loading…</p>}
      {trip.error && <Alert tone="error">{trip.error}</Alert>}
      {trip.data && (
        <div className="flex flex-col gap-5">
          <div>
            <Eyebrow>{trip.data.route.name}</Eyebrow>
            <p className="mt-1 flex items-baseline gap-3">
              <span className="board-time text-3xl">{formatTime(trip.data.departureAt, timezone)}</span>
              <StatusBadge status={trip.data.status} />
            </p>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
              {formatDate(trip.data.departureAt, timezone)} · {trip.data.bus.name} ·{' '}
              {trip.data.bus.plateNumber}
            </p>
            {trip.data.note && (
              <p className="mt-2 text-sm font-medium" style={{ color: 'var(--signal-delayed)' }}>
                {trip.data.note}
              </p>
            )}
          </div>

          <Card>
            <Eyebrow>{t('f.callingAt')}</Eyebrow>
            <ol className="mt-2 flex flex-col gap-1.5">
              {trip.data.route.routeStops.map((stop) => (
                <li key={stop.id} className="flex items-baseline gap-3 text-sm">
                  <span className="w-14 tabular" style={{ color: 'var(--color-muted)' }}>
                    +{stop.arrivalOffsetMin}m
                  </span>
                  <span>
                    {stop.stop.name}
                    <span style={{ color: 'var(--color-faint)' }}> · {stop.stop.city}</span>
                  </span>
                </li>
              ))}
            </ol>
          </Card>

          <div>
            <div className="flex items-baseline justify-between">
              <Eyebrow>{t('trips.manifest')}</Eyebrow>
              <span className="text-xs tabular" style={{ color: 'var(--color-muted)' }}>
                {trip.data.seatsBooked} of {trip.data.capacity} seats
              </span>
            </div>
            {trip.data.manifest.length === 0 ? (
              <p className="mt-2 text-sm" style={{ color: 'var(--color-muted)' }}>
                {t('trips.manifestEmpty')}
              </p>
            ) : (
              <table className="mt-2 w-full border-collapse text-sm">
                <thead>
                  <tr>
                    {['Seat', 'Passenger', 'Reference', 'Checked in'].map((header) => (
                      <th
                        key={header}
                        className="border-b px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wider"
                        style={{ borderColor: 'var(--color-line)', color: 'var(--color-faint)' }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {trip.data.manifest.map((row) => (
                    <tr key={row.seatCode}>
                      <td className="border-b px-2 py-1.5 tabular font-medium" style={{ borderColor: 'var(--color-line)' }}>
                        {row.seatCode}
                      </td>
                      <td className="border-b px-2 py-1.5" style={{ borderColor: 'var(--color-line)' }}>
                        {row.passengerName}
                      </td>
                      <td className="border-b px-2 py-1.5 tabular" style={{ borderColor: 'var(--color-line)' }}>
                        {row.reference}
                      </td>
                      <td className="border-b px-2 py-1.5" style={{ borderColor: 'var(--color-line)' }}>
                        {row.checkedInAt ? formatTime(row.checkedInAt, timezone) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {trip.data.status !== 'CANCELLED' && (
            <div>
              <Eyebrow>{t('f.operations')}</Eyebrow>

              {departsWithinTwoHours && (
                <div className="mt-2">
                  <Alert tone="warning">
                    This departure is within two hours — passengers may already be travelling.
                  </Alert>
                </div>
              )}

              {notice && (
                <div className="mt-2">
                  <Alert tone="success">{notice}</Alert>
                </div>
              )}
              {actionError && (
                <div className="mt-2">
                  <Alert tone="error">{actionError}</Alert>
                </div>
              )}

              <div className="mt-3 flex flex-col gap-4">
                <div className="flex items-end gap-2">
                  <Field label={t('trips.delayBy')} htmlFor="delay-min">
                    <Input
                      id="delay-min"
                      type="number"
                      min={0}
                      max={1440}
                      className="w-28"
                      value={delayMinutes}
                      onChange={(e) => setDelayMinutes(Number(e.target.value))}
                    />
                  </Field>
                  <Button
                    loading={busy}
                    onClick={() =>
                      act('delay', { minutes: delayMinutes }, {
                        title: `Delay this departure by ${delayMinutes} minutes?`,
                        body: `${booked} booked passenger${booked === 1 ? '' : 's'} will be notified.`,
                        confirmLabel: t('trips.applyDelay'),
                        destructive: false,
                      })
                    }
                  >
                    {t('trips.applyDelay')}
                  </Button>
                </div>

                <div className="flex items-end gap-2">
                  <Field
                    label={t('trips.noteForPax')}
                    htmlFor="trip-note"
                    hint={t('trips.noteHint')}
                  >
                    <Input id="trip-note" value={note} onChange={(e) => setNote(e.target.value)} />
                  </Field>
                  <Button
                    variant="secondary"
                    loading={busy}
                    disabled={!note.trim()}
                    onClick={() =>
                      act('note', { note: note.trim() }, {
                        title: `Send this note to ${booked} booked passenger${booked === 1 ? '' : 's'}?`,
                        confirmLabel: t('trips.sendNote'),
                        destructive: false,
                      })
                    }
                  >
                    {t('trips.sendNote')}
                  </Button>
                </div>

                <div>
                  <Button
                    variant="danger"
                    loading={busy}
                    onClick={() =>
                      act('cancel', { autoRefund: true }, {
                        title: 'Cancel this departure?',
                        body: `${booked} booked passenger${
                          booked === 1 ? '' : 's'
                        } will be notified and refunded automatically. This cannot be undone.`,
                        confirmLabel: t('trips.cancelDep'),
                      })
                    }
                  >
                    {t('trips.cancelDep')}
                  </Button>
                  <p className="mt-1 text-xs" style={{ color: 'var(--color-muted)' }}>
                    {t('trips.cancelHint')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {dialog}
    </Drawer>
  );
}

function AdHocDrawer({
  timezone,
  onClose,
  onCreated,
}: {
  timezone: string;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const t = useT();
  const routes = useResource<Array<{ id: string; name: string }>>('/admin/routes');
  const buses = useResource<Array<{ id: string; name: string; plateNumber: string }>>('/admin/buses');
  const [form, setForm] = useState({
    routeId: '',
    busId: '',
    date: todayIn(timezone),
    departureTime: '08:00',
    note: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function create() {
    setSaving(true);
    setError(null);
    try {
      await api.post('/admin/trips', { ...form, note: form.note || undefined });
      await onCreated();
    } catch (err) {
      setError(errorMessage(err, 'Could not create this departure.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      open
      title={t('trips.oneOff')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('f.cancel')}
          </Button>
          <Button onClick={create} loading={saving}>
            {t('trips.create')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
          A single extra departure that is not part of a repeating timetable.
        </p>
        <Field label={t('f.route')} htmlFor="ah-route">
          <Select id="ah-route" value={form.routeId} onChange={(e) => setForm({ ...form, routeId: e.target.value })}>
            <option value="">{t('trips.chooseRoute')}</option>
            {(routes.data ?? []).map((route) => (
              <option key={route.id} value={route.id}>
                {route.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('f.bus')} htmlFor="ah-bus">
          <Select id="ah-bus" value={form.busId} onChange={(e) => setForm({ ...form, busId: e.target.value })}>
            <option value="">{t('trips.chooseBus')}</option>
            {(buses.data ?? []).map((bus) => (
              <option key={bus.id} value={bus.id}>
                {bus.name} · {bus.plateNumber}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('f.date')} htmlFor="ah-date">
            <Input id="ah-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </Field>
          <Field label={t('trips.departureTime')} htmlFor="ah-time" hint={`Local to ${timezone}.`}>
            <Input
              id="ah-time"
              type="time"
              value={form.departureTime}
              onChange={(e) => setForm({ ...form, departureTime: e.target.value })}
            />
          </Field>
        </div>
        <Field label={t('f.note')} htmlFor="ah-note" hint={t('trips.noteOptional')}>
          <Input id="ah-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </Field>
      </div>
    </Drawer>
  );
}
